import type {
  SubscriptionCheckoutFrontendInput,
  SubscriptionUpdateFrontendInput,
  ChangeCurrencyAndPlanInputDTO,
} from '@kanbu/schema/contracts';
import {
  BillingInterval,
  SubscriptionTier,
  type Currency,
} from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TRPCClientErrorLike } from '@trpc/client';
import { toast } from '@utima/ui';

import { invalidateCurrentOrganization } from '@/services/invalidateCurrentOrganization';
import { trpc } from '@/services/trpc';

interface BillingPortalInput {
  returnUrl?: string;
}

function createErrorHandler(
  prefix: string,
): (error: TRPCClientErrorLike<any>) => void {
  return error => {
    const message = error.message ?? 'Unknown error';
    toast.error(`${prefix}: ${message}`);
  };
}

export function useSubscriptionManager(organizationId: string) {
  const utils = trpc.useUtils();
  const { t } = useLingui();

  const handleCreateCheckoutError = createErrorHandler(
    t`Failed to create checkout session`,
  );
  const handleUpdateError = createErrorHandler(
    t`Failed to update subscription`,
  );
  const handleCancelError = createErrorHandler(
    t`Failed to cancel subscription`,
  );
  const handleBillingPortalError = createErrorHandler(
    t`Failed to create billing portal session`,
  );
  const handleTopUpError = createErrorHandler(
    t`Failed to create top-up session`,
  );
  const handleChangeCurrencyAndPlanError = createErrorHandler(
    t`Failed to change currency/plan`,
  );

  const createCheckoutMutation = trpc.subscriptions.createCheckout.useMutation({
    onError: handleCreateCheckoutError,
  });

  const updateMutation = trpc.subscriptions.update.useMutation({
    onSuccess: async data => {
      if (data.billingPortalUrl) {
        toast.info(t`Payment confirmation needed`, {
          description: t`Redirecting to Stripe to complete the payment for your plan change.`,
          duration: 5000,
        });

        window.location.href = data.billingPortalUrl;
      } else {
        toast.success(t`Subscription updated successfully.`);
        await utils.subscriptions.getActive.invalidate({ organizationId });
        await utils.organizations.findOne.invalidate({ id: organizationId });
        await invalidateCurrentOrganization(organizationId);
      }
    },
    onError: handleUpdateError,
  });

  const cancelMutation = trpc.subscriptions.cancel.useMutation({
    onSuccess: async () => {
      toast.success(t`Subscription cancelled successfully.`);
      await utils.subscriptions.getActive.invalidate({ organizationId });
    },
    onError: handleCancelError,
  });

  const billingPortalMutation =
    trpc.subscriptions.createBillingPortal.useMutation({
      onError: handleBillingPortalError,
    });

  const topUpMutation = trpc.subscriptions.topUpBalance.useMutation({
    onError: handleTopUpError,
  });

  const changeCurrencyForFreeTierMutation =
    trpc.subscriptions.changeCurrencyAndPlan.useMutation({
      onSuccess: async () => {
        toast.success(t`Billing currency updated successfully.`);
        await utils.organizations.findOne.invalidate({ id: organizationId });
        await invalidateCurrentOrganization(organizationId);
        await utils.subscriptions.getActive.invalidate({ organizationId });
        await utils.subscriptions.getTierDetails.invalidate();
      },
      onError: handleChangeCurrencyAndPlanError,
    });

  const isLoading = [
    createCheckoutMutation,
    updateMutation,
    cancelMutation,
    billingPortalMutation,
    topUpMutation,
    changeCurrencyForFreeTierMutation,
  ].some(mutation => mutation.isPending);

  const subscribe = async (input: SubscriptionCheckoutFrontendInput) => {
    const result = await createCheckoutMutation.mutateAsync({
      organizationId,
      ...input,
    });

    return result || { url: null };
  };

  const updateSubscription = async (input: SubscriptionUpdateFrontendInput) => {
    return updateMutation.mutateAsync({
      organizationId,
      ...input,
    });
  };

  const createBillingPortal = async (input: BillingPortalInput) => {
    return billingPortalMutation.mutateAsync({
      organizationId,
      ...input,
    });
  };

  const cancelSubscription = async () => {
    // Self-serve cancellation is end-of-term (reversible); the customer keeps
    // service until the paid period ends. Immediate cancel is SuperAdmin-only.
    return cancelMutation.mutateAsync({ organizationId, atPeriodEnd: true });
  };

  const topUpBalance = async (amount: number) => {
    const successUrl = window.location.href;
    const cancelUrl = window.location.href;

    const result = await topUpMutation.mutateAsync({
      organizationId,
      amount,
      successUrl,
      cancelUrl,
    });

    return result || { url: undefined };
  };

  const changeBillingCurrencyForFreeTier = async (targetCurrency: Currency) => {
    return changeCurrencyForFreeTierMutation.mutateAsync({
      organizationId,
      targetCurrency,
      targetTier: SubscriptionTier.Free,
      targetBillingInterval: BillingInterval.Month,
    } as ChangeCurrencyAndPlanInputDTO);
  };

  return {
    isLoading,
    subscribe,
    updateSubscription,
    createBillingPortal,
    cancelSubscription,
    topUpBalance,
    changeBillingCurrencyForFreeTier,
  };
}
